import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { ChevronRight } from 'lucide-react';

/**
 * The DB grid's right-click menu (moved out of DbManagerResultsTab), now with
 * submenus: an item `{ label, submenu: [...] }` opens a flyout beside its row
 * on hover, click or ArrowRight (owner, 30.09.2026: Copy ▸ and Filter ▸).
 *
 * Items: { label, run, disabled, title, danger, checked, separator, submenu }.
 *
 * The flyout is a second fixed panel, not a child of the first: the menu panel
 * (.dyn-var-suggestions) clips its overflow, so a nested absolute panel would
 * be cut off. Both panels stop mousedown, so the results tab's
 * close-on-outside-mousedown never sees a click inside either.
 *
 * Keyboard: ↑/↓ move, → / Enter opens a submenu, ← closes it, Enter runs an
 * item. Escape is the results tab's (it closes the menu without touching
 * staged edits). `data-dbm-context-menu` tells the grid's key guard to stand
 * aside while the menu is open.
 */
const ROW_H = 29;
const PANEL_W = 230;

const actionable = (items) => items.map((it, i) => (it.separator ? null : i)).filter((i) => i != null);

function MenuRow({ item, active, onEnter, onActivate, rowRef }) {
  return (
    <div
      ref={rowRef}
      // menuitemradio only when the item HAS a checked state — a plain action
      // announced as a radio would tell a screen reader it is one of a set.
      role={item.checked === undefined ? 'menuitem' : 'menuitemradio'}
      aria-checked={item.checked === undefined ? undefined : !!item.checked}
      aria-haspopup={item.submenu ? 'menu' : undefined}
      aria-disabled={item.disabled || undefined}
      className={`dyn-var-suggestion-item${!item.disabled && active ? ' dyn-var-suggestion-item--active' : ''}`}
      style={{
        fontSize: 12,
        alignItems: 'center',
        color: item.danger ? '#ef4444' : undefined,
        opacity: item.disabled ? 0.45 : undefined,
        cursor: item.disabled ? 'default' : undefined,
      }}
      title={item.disabled ? item.title : undefined}
      onMouseEnter={onEnter}
      onClick={onActivate}
    >
      {/* A fixed-width slot so labels line up whether or not the menu has
          checkable items in it. */}
      {item.checked !== undefined && (
        <span style={{ display: 'inline-block', width: 12, opacity: item.checked ? 1 : 0 }}>✓</span>
      )}
      <span style={{ flex: 1 }}>{item.label}</span>
      {item.submenu && <ChevronRight size={12} aria-hidden style={{ opacity: 0.6 }} />}
    </div>
  );
}

function Separator() {
  return <div style={{ height: 1, background: 'rgba(128,128,128,0.25)', margin: '4px 6px' }} />;
}

export default function DbGridContextMenu({ items, x, y, onClose }) {
  const [hover, setHover] = useState(-1); // index into items
  const [sub, setSub] = useState(null); // { index, left, top, flip }
  const [subHover, setSubHover] = useState(-1);
  const rowRefs = useRef({});
  const subRef = useRef(null);
  const subItems = sub ? items[sub.index]?.submenu || [] : [];

  const itemCount = items.filter((it) => !it.separator).length;
  const left = Math.max(4, Math.min(x, window.innerWidth - PANEL_W));
  const top = Math.max(4, Math.min(y, window.innerHeight - (itemCount * ROW_H + 16)));

  const openSub = (index) => {
    const item = items[index];
    if (!item?.submenu || item.disabled) return;
    const rect = rowRefs.current[index]?.getBoundingClientRect();
    if (!rect) return;
    const count = item.submenu.filter((it) => !it.separator).length;
    const flip = rect.right + PANEL_W > window.innerWidth - 4;
    const subLeft = flip ? Math.max(4, rect.left - PANEL_W + 4) : rect.right - 4;
    const subTop = Math.max(4, Math.min(rect.top - 4, window.innerHeight - (count * ROW_H + 16)));
    setSub({ index, left: subLeft, top: subTop, flip, anchorLeft: rect.left });
    setSubHover(-1);
  };

  const run = (item) => {
    if (!item || item.disabled || item.submenu) return;
    onClose();
    item.run();
  };

  // Keyboard, while open. Capture phase so it lands before the grid's own
  // handlers; the grid's key guard also stands aside (data-dbm-context-menu).
  const stateRef = useRef();
  stateRef.current = { hover, sub, subHover, items, subItems };
  useEffect(() => {
    const onKey = (e) => {
      const { hover: h, sub: s, subHover: sh, items: list, subItems: subList } = stateRef.current;
      // Next actionable index in `dir` (+1 / -1), wrapping; from -1 lands on
      // the first (or, going up, the last) item.
      const step = (arr, from, dir) => {
        const idx = actionable(arr);
        if (idx.length === 0) return -1;
        const at = idx.indexOf(from);
        if (at === -1) return dir > 0 ? idx[0] : idx[idx.length - 1];
        return idx[(at + dir + idx.length) % idx.length];
      };
      const k = e.key;
      if (!['ArrowDown', 'ArrowUp', 'ArrowRight', 'ArrowLeft', 'Enter'].includes(k)) return;
      e.preventDefault();
      e.stopImmediatePropagation();
      if (s) {
        if (k === 'ArrowDown') setSubHover(step(subList, sh, 1));
        else if (k === 'ArrowUp') setSubHover(step(subList, sh, -1));
        else if (k === 'ArrowLeft') setSub(null);
        else if (k === 'Enter') run(subList[sh]);
        return;
      }
      if (k === 'ArrowDown') setHover(step(list, h, 1));
      else if (k === 'ArrowUp') setHover(step(list, h, -1));
      else if ((k === 'ArrowRight' || k === 'Enter') && list[h]?.submenu) {
        openSub(h);
        setSubHover(step(list[h].submenu, -1, 1));
      }
      else if (k === 'Enter') run(list[h]);
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // A flipped flyout is placed by an assumed width first; once it has
  // rendered, butt its right edge against the menu using its real width.
  useLayoutEffect(() => {
    if (!sub?.flip || !subRef.current) return;
    const want = Math.max(4, sub.anchorLeft - subRef.current.offsetWidth + 4);
    if (Math.abs(want - sub.left) > 0.5) setSub((prev) => (prev ? { ...prev, left: want } : prev));
  }, [sub]);

  const panelProps = {
    onMouseDown: (e) => e.stopPropagation(),
    onContextMenu: (e) => e.preventDefault(),
    role: 'menu',
    'data-dbm-context-menu': '',
  };

  return ReactDOM.createPortal(
    <>
      <div className="dyn-var-suggestions" style={{ left, top, minWidth: 200, maxHeight: 'none' }} {...panelProps}>
        {items.map((item, i) =>
          item.separator ? (
            <Separator key={`sep-${i}`} />
          ) : (
            <MenuRow
              key={item.label}
              item={item}
              active={hover === i || sub?.index === i}
              rowRef={(el) => {
                rowRefs.current[i] = el;
              }}
              onEnter={() => {
                setHover(i);
                if (item.submenu) openSub(i);
                else setSub(null);
              }}
              onActivate={() => (item.submenu ? openSub(i) : run(item))}
            />
          )
        )}
      </div>
      {sub && subItems.length > 0 && (
        <div
          ref={subRef}
          className="dyn-var-suggestions dbm-ctx-subpanel"
          style={{ left: sub.left, top: sub.top, minWidth: 200, maxHeight: 'none' }}
          aria-label={items[sub.index]?.label}
          {...panelProps}
        >
          {subItems.map((item, i) =>
            item.separator ? (
              <Separator key={`ssep-${i}`} />
            ) : (
              <MenuRow
                key={item.label}
                item={item}
                active={subHover === i}
                onEnter={() => setSubHover(i)}
                onActivate={() => run(item)}
              />
            )
          )}
        </div>
      )}
    </>,
    document.body
  );
}
